/* eslint-disable @typescript-eslint/no-explicit-any */
import { ActiveSelection, Canvas, FabricObject, Point } from 'fabric';

import { TextEntity } from '../../metaBullets/classes/TextEntity';

const isActiveSelection = (object: FabricObject): object is ActiveSelection =>
  '_objects' in object && object.type === 'activeselection';

const isGroup = (object: FabricObject): object is ActiveSelection =>
  '_objects' in object && object.type === 'group';

export enum SnapLineEnum {
  SnapLineX = 'snap-line-x',
  SnapLineY = 'snap-line-y',
}

/**
 * Gets valid objects to snap to
 */
const getTextFromGroups = (obj: FabricObject): FabricObject | FabricObject[] => {
  if (isGroup(obj)) {
    const textObjects = obj._objects.filter((child) => child.type === 'textbox');
    return [obj, ...textObjects];
  }
  return obj;
};

export const getSnappableObjects = (canvas: Canvas, currentObject: FabricObject): FabricObject[] =>
  canvas
    .getObjects()
    .flatMap(getTextFromGroups)
    .filter((obj) => {
      // Skip the current object and snap lines
      if (
        obj.id === currentObject.id ||
        obj.name === SnapLineEnum.SnapLineX ||
        obj.name === SnapLineEnum.SnapLineY
      ) {
        return false;
      }

      // Skip locked objects
      if (obj.isLocked) {
        return false;
      }

      // Check if object is in selection
      if (isActiveSelection(currentObject)) {
        if (currentObject._objects.some((child) => obj.id === child.id)) {
          return false;
        }
      }

      // Пропускаем текстовые сущности из метабуллитов
      if (TextEntity.isTextEntity(obj) && (obj as TextEntity).metaBulletId) {
        return false;
      }

      // Accept common snappable object types
      return !['metadata'].includes(obj.type) && currentObject.idGroup !== obj.id;
    });

/**
 * Get key points of the object (top, middle, bottom, left, center, right)
 */
export const getObjectSnapPoints = (obj: FabricObject) => {
  const bbox = obj.getBoundingRect();

  return {
    horizontal: [
      bbox.top, // top edge
      bbox.top + bbox.height / 2, // middle
      bbox.top + bbox.height, // bottom edge
    ],
    vertical: [
      bbox.left, // left edge
      bbox.left + bbox.width / 2, // center
      bbox.left + bbox.width, // right edge
    ],
  };
};

export const cleanStateFromSnaplines = (state: any) => {
  if (!state.objects) return state;
  return {
    ...state,
    objects: state.objects.filter(
      (obj: FabricObject) =>
        obj.name !== SnapLineEnum.SnapLineX && obj.name !== SnapLineEnum.SnapLineY,
    ),
  };
};

/**
 * Check if a snap line is blocked by any object between source and target
 */
export const isSnapLineBlocked = (
  canvas: Canvas,
  sourceObj: FabricObject,
  targetObj: FabricObject,
  start: Point,
  end: Point,
  isHorizontal: boolean,
): boolean => {
  // Get all objects that could potentially block the line
  const potentialBlockers = canvas.getObjects().filter((obj) => {
    // Skip source, target, guidelines, and non-snappable objects
    if (
      obj.id === sourceObj.id ||
      obj.id === targetObj.id ||
      obj.name === SnapLineEnum.SnapLineX ||
      obj.name === SnapLineEnum.SnapLineY ||
      obj.isLocked ||
      ['metadata'].includes(obj.type)
    ) {
      return false;
    }

    // Skip objects that are part of a selection
    if (isActiveSelection(targetObj) && targetObj._objects.some((child) => obj.id === child.id)) {
      return false;
    }

    return true;
  });

  // Get bounding boxes for source and target
  const sourceBBox = sourceObj.getBoundingRect();
  const targetBBox = targetObj.getBoundingRect();

  // Check if any object intersects with the line between source and target objects
  return potentialBlockers.some((obj) => {
    const blockerBBox = obj.getBoundingRect();

    if (isHorizontal) {
      const lineY = start.y;

      // Object blocks if:
      // 1. It vertically contains the snapline (its top is above the line and bottom is below)
      // 2. It horizontally intersects the space between source and target
      if (blockerBBox.top <= lineY && blockerBBox.top + blockerBBox.height >= lineY) {
        // Determine the actual horizontal range of the snapline
        // This should be the gap between the source and target objects
        let lineStartX, lineEndX;

        if (sourceBBox.left + sourceBBox.width < targetBBox.left) {
          // Source is to the left of target
          lineStartX = sourceBBox.left + sourceBBox.width;
          lineEndX = targetBBox.left;
        } else if (targetBBox.left + targetBBox.width < sourceBBox.left) {
          // Target is to the left of source
          lineStartX = targetBBox.left + targetBBox.width;
          lineEndX = sourceBBox.left;
        } else {
          // Objects overlap horizontally, no real gap to check
          return false;
        }

        // Check if blocker is positioned between the source and target
        return blockerBBox.left < lineEndX && blockerBBox.left + blockerBBox.width > lineStartX;
      }
      return false;
    } else {
      const lineX = start.x;

      // Object blocks if:
      // 1. It horizontally contains the snapline (its left is to the left of the line and right is to the right)
      // 2. It vertically intersects the space between source and target
      if (blockerBBox.left <= lineX && blockerBBox.left + blockerBBox.width >= lineX) {
        // Determine the actual vertical range of the snapline
        // This should be the gap between the source and target objects
        let lineStartY, lineEndY;

        if (sourceBBox.top + sourceBBox.height < targetBBox.top) {
          // Source is above target
          lineStartY = sourceBBox.top + sourceBBox.height;
          lineEndY = targetBBox.top;
        } else if (targetBBox.top + targetBBox.height < sourceBBox.top) {
          // Target is above source
          lineStartY = targetBBox.top + targetBBox.height;
          lineEndY = sourceBBox.top;
        } else {
          // Objects overlap vertically, no real gap to check
          return false;
        }

        // Check if blocker is positioned between the source and target
        return blockerBBox.top < lineEndY && blockerBBox.top + blockerBBox.height > lineStartY;
      }
      return false;
    }
  });
};
